import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input, Tab } from 'semantic-ui-react';
import Layout from '~/components/Layout'
import i18n from '~/components/i18n';
import { useDebounce } from 'use-lodash-debounce';
import notify from '~/components/Notify';
import { SurveyQuery } from '~/QueryHooks/Survey';
import TableComponent from '~/components/TableComponent';
import { RouteTypes } from '~/routes/constants';
import Dialog from '~/components/Dialog';
import SurveyService from '~/api/services/survey';
import { SURVEY_STATUS_KEYS } from '~/Constants';

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Status'),
    key: 'statusLabel'
  },
  {
    name: i18n.t('Survey-ID'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Survey title'),
    key: 'title'
  },
  {
    name: i18n.t('Questions'),
    key: 'questionsCount'
  },
  {
    name: i18n.t('Start / End'),
    key: 'start',
    type: 'startEndSurvey',
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
];

const getActions = () => [
  {
    popupMessage: i18n.t('Edit entries'),
    type: 'link',
    name: 'pencil',
    size: 'large',
    link: RouteTypes.SURVEY_EDIT_ENTRIES,
    display: [SURVEY_STATUS_KEYS.live],
    verifiedActionKey: 'completed',
    verifiedAction: 1
  },
  {
    popupMessage: i18n.t('Request entries'),
    type: 'link',
    name: 'icon-answer-survey',
    size: 'large',
    link: RouteTypes.SURVEY_REQUEST_ENTRIES,
    display: [SURVEY_STATUS_KEYS.live],
    verifiedActionKey: 'completed',
    verifiedAction: 0
  },
  {
    popupMessage: i18n.t('Questions and Answers'),
    type: 'link',
    name: 'eye',
    size: 'large',
    link: RouteTypes.SURVEY_SHOW_ANSWERS,
    verifiedActionKey: 'completed',
    verifiedAction: 1,
    replaceKey: ':idSurvey'
  }
]

const Survey: React.FC = () => {
  const { t } = useTranslation()
  const [isBusy, setIsBusy] = useState(false)
  const [search, setSearch] = useState('')
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [surveyPreviewData, setSurveyPreviewData] = useState(null)
  const debouncedSearchValue = useDebounce(search, 300)
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 30,
    search: '',
    status: 'actual',
    column: '',
    sort: ''
  })

  const {
    isLoading: isBusyData,
    data: surveyData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = SurveyQuery.getSurveys({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  const {
    data: surveyCount,
    isLoading: isBusySurveyCount,
    refetch: fetchCount
  } = SurveyQuery.getSurveysCount({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  const {
    data: surveyOptions,
    refetch: fetchOptions,
    isLoading: isBusyOptions
  } = SurveyQuery.getSurveysOptions()
  
  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
    fetchOptions()
  }, [t])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData();
      fetchCount()
    }
  }, [dataFiltered]);

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  function renderSearch() {
    return (
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    );
  }

  async function fetchPreviewSurveyData(id) {
    setIsBusy(true)
    try {
      const response = await SurveyService.editSurveysQuestions(id)
      setSurveyPreviewData(response.data)
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setIsBusy(false)
  }

  function handleClickAction(row, type) {
    switch (type) {
      case 'show':
        return fetchPreviewSurveyData(row?.id)
    }
  }

  function renderContent() {
    return <TableComponent
      actions={getActions()}
      isFetchingData={isFetchingData}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleClickAction={handleClickAction}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      const status = surveyOptions.find((_, indexOption) => indexOption === activeIndex);
      setDataFiltered({
        ...dataFiltered,
        status: status.key,
        page: 0
      });
      setActiveTabIndex(activeIndex)
    }
  }

  function renderPanes() {
    const panes = surveyOptions.map((option) => {
      const countIndex = surveyCount.findIndex((count) => count.status == option.key);
      return {
        menuItem: `${option.label}  (${surveyCount[countIndex]?.count || 0})`,
        render: () => renderContent()
      };
    });

    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        activeIndex={activeTabIndex}
        panes={panes}
        onTabChange={(_, data) => handleTabChange(data.activeIndex)}
      />
    );
  }

  function renderSurveyPreview() {
    return <Dialog
      isDialogOpen={surveyPreviewData || isBusy}
      isLoading={isBusy}
      handleClose={() => setSurveyPreviewData(null)}
      textHeader={''}
      textClose={t('Close')}
      textContent={<>
      </>}
    />
  }

  const { filtered, rows } = surveyData ? surveyData : { filtered: 0, rows: [] };
  const loading = isBusyOptions || isBusyData || isBusySurveyCount || isBusy

  return <Layout
    title={t('Surveys')}
    filters={renderSearch()}
    segmented
    isBusy={loading}
  >
    {renderSurveyPreview()}
    {surveyData && surveyCount && surveyOptions && renderPanes()}
  </Layout>
}
export default Survey